import * as turf from "@turf/turf";
import { Feature, GeoJsonProperties, Polygon } from "geojson";
import { LngLat } from "mapbox-gl";
import api from "../api/api";
import { GeoCoordinate } from "../interfaces/GeoCoordinate";
import IAddressLocation from "../interfaces/IAddressLocation";
import IGeoCoordinate from "../interfaces/IGeoCoordinate";
import IMapboxPlace from "../interfaces/IMapboxPlace";
import IMapboxSearchResponse from "../interfaces/IMapboxSearchResponse";
import { shortenAddress } from "../utils/addressFormat";

export default class MapboxApiService {
    public async addressLocationSearch(query: string, proximity: LngLat, limit: number = 10): Promise<IAddressLocation[]> {
        const response = await api.get<IMapboxSearchResponse>(`/mapbox/search` +
            `?query=${query}&country=US&types=place,district,locality,neighborhood,address&autocomplete=true&fuzzyMatch=true&language=en&proximity=${proximity.lng},${proximity.lat}&limit=${limit}`);

        return response.data.features.map((f: any): IAddressLocation => ({
            address: shortenAddress(f.place_name, { includeZip: true }),
            coordinate: new GeoCoordinate(f.center[1], f.center[0]),
            types: f.properties.accuracy === "street" ? ["street"] : f.place_type,
        }));
    }

    public async getMapboxParcelInfo(point: LngLat): Promise<IMapboxPlace> {
        const response = await api.get(`/mapbox/parcel?coordinates=${point.lng},${point.lat}`);

        const features = response.data.features;
        const addressFeature = features.find((f: any) => f.id.includes("address"));
        const poiFeature = features.find((f: any) => f.id.includes("poi"));
        const neighborhoodFeature = features.find((f: any) => f.id.includes("neighborhood"));
        const postcodeFeature = features.find((f: any) => f.id.includes("postcode"));
        const placeFeature = features.find((f: any) => f.id.includes("place"));
        const regionFeature = features.find((f: any) => f.id.includes("region"));
        const countryFeature = features.find((f: any) => f.id.includes("country"));

        let address = null;

        if (addressFeature) {
            address = addressFeature.place_name ? addressFeature.place_name : "";
        }
        else if (poiFeature) {
            address = poiFeature.place_name;

            const text = poiFeature.text;
            if (text && poiFeature.place_name.includes(text))
                address = address.replace(`${text}, `, "");
        }

        let region: string = regionFeature ? regionFeature.text.toLowerCase() : null;

        if (region) {
            if (region.includes("virginia"))
                region = "VA";

            if (region.includes("maryland"))
                region = "MD";

            if (region.includes("district of columbia"))
                region = "DC";
        }

        return {
            address: shortenAddress(address, { includeZip: true }),
            neighborhood: neighborhoodFeature ? neighborhoodFeature.text : null,
            postcode: postcodeFeature ? postcodeFeature.text : null,
            place: placeFeature ? placeFeature.text : null,
            region: region,
            country: countryFeature ? countryFeature.text : null,
        }
    }

    public async getIsochrone(point: IGeoCoordinate, minutes: number): Promise<Feature<Polygon, GeoJsonProperties>> {
        const response = await api.get(`/mapbox/isochrone?coordinates=${point.longitude},${point.latitude}&contours_minutes=${minutes}`);

        return turf.polygon([response.data.features[0].geometry.coordinates]);
    }
}